import React, { useContext, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FeatureKeys } from '@owl-frontend/api-client';
import type { EmailSubscriptions } from '@owl-frontend/api-client';
import { useEffectWithPrev, toast } from '@owl-frontend/components';
import {
  Locale,
  SessionContext,
} from '../../context/AppContainer/AppContainer';
import { actions as sessionActions } from '../../data/session/logic';
import type { StoreState } from '../../data/store';
import { actions } from '../../pages/users/data/logic';

type EmailSubscriptionHookResult = [
  EmailSubscriptions | undefined,
  (updatedEmailSubscriptions: EmailSubscriptions) => void
];

function useEmailSubscriptionSettings(): EmailSubscriptionHookResult {
  const { messages } = useContext(Locale);
  const { current: session, features } = useContext(SessionContext);
  const dispatch = useDispatch();
  const updateEmailSubscriptionsAction = useSelector(
    (s: StoreState) => s['users'].actions.updateEmailSubscriptions
  );

  // Only display email subscriptions that are enabled for a tenant through feature flags
  // The "key" corresponds to the email-key (which is the way it will be the partition key in dynamo DB)
  const getEnabledEmailSubscriptions = React.useCallback(
    (
      emailSubscriptions: EmailSubscriptions | undefined
    ): EmailSubscriptions => {
      if (!emailSubscriptions) {
        return {};
      }
      return Object.keys(emailSubscriptions).reduce((acc, key) => {
        if (key === '24HrNotify') {
          if (
            features[FeatureKeys.EmailNotify24hrFileReady] ||
            features[FeatureKeys.EmailNotifyAllFileReady]
          ) {
            acc[key] = emailSubscriptions[key];
          }
        }
        return acc;
      }, {});
    },
    [features]
  );

  const emailSubscriptions = useMemo(
    () => getEnabledEmailSubscriptions(session?.userData?.emailSubscriptions),
    [getEnabledEmailSubscriptions, session?.userData?.emailSubscriptions]
  );

  const updateEmailSubscriptions = (
    updatedEmailSubscriptions: EmailSubscriptions
  ) => {
    dispatch(
      actions.updateEmailSubscriptions({
        emailSubscriptions: updatedEmailSubscriptions,
      })
    );
  };

  useEffectWithPrev(
    (prevStatus) => {
      if (prevStatus !== 'pending') {
        return;
      }
      if (updateEmailSubscriptionsAction.status === 'rejected') {
        toast({
          key: 'TOGGLE_EMAIL_SUBSCRIPTION_FAILURE',
          type: 'error',
          message: messages['users.updateEmailSettings.error'],
        });
        dispatch(sessionActions.current());
      }
    },
    [messages, updateEmailSubscriptionsAction.status]
  );
  return [emailSubscriptions, updateEmailSubscriptions];
}

export default useEmailSubscriptionSettings;
